import CollapsibleGroup from "../../../views/cloud/CreateCloudView/CreateCloudComponents/CollapsibleGroup";
import RequiredTitle from "../../../elements/RequiredTitle";
import { Button, Input } from "reactstrap";
import {
  FieldsList,
  createField,
} from "../Fields/FieldTypeSpecific/FieldTypeSubitem";
import { Field, Module } from "../../../types/TenantInterfaces";
import { ChangeEvent, Dispatch, SetStateAction, useState } from "react";
import StrUtils from "../../../utils/StrUtils";
import CreateFieldDrawer from "../Fields/CreateFieldDrawer";
import EditFieldDrawer from "../Fields/EditFieldDrawer";
import { callFunctionIfExists } from "../../../utils/GeneralUtils";
import RecordNameTemplate from "../../Records/RecordNameTemplate/RecordNameTemplate";

interface Props {
  modSchema: Module;
  setModSchema: Dispatch<SetStateAction<Module>>;
  uploadedFields: Field[];
}

export default function ConfigureModule({
  modSchema,
  setModSchema,
  uploadedFields,
}: Props) {
  const [createDrawer, setCreateDrawer] = useState(false);
  const [editField, setEditField] = useState<Field>();

  return (
    <>
      <CollapsibleGroup
        icon="mdi mdi-database-edit-outline"
        title="Data Source Information"
        description="Enter the name and description for your data source."
        className="mb-4"
      >
        <RequiredTitle title="Name" size="md" />
        <Input
          bsSize="sm"
          placeholder="The name of this data source"
          onChange={handleNameChange}
          value={modSchema.Name}
        />
        <h4 className="mt-4 mb-1">API Name</h4>
        <Input
          bsSize="sm"
          placeholder="The API Name of this data source"
          onChange={handleAPINameChange}
          value={modSchema.APIName}
          className="mb-3"
        />
        <small className="text-dark">
          <i className="mdi mdi-alert mr-2" />
          This section can be collapsed by clicking the top section labeled{" "}
          <span className="font-weight-bold">Data Source Information</span>
        </small>
      </CollapsibleGroup>
      <CollapsibleGroup
        icon="mdi mdi-format-list-bulleted"
        title="Fields"
        description={
          <>
            The fields we calculated from the file you provided.
            <div style={{ lineHeight: 1 }}>
              Please review their field types to ensure they will meet your
              needs.
            </div>
          </>
        }
        className="mb-4"
        startOpen={false}
      >
        <div className="d-flex justify-content-between align-items-center border-bottom border-superlight mb-3 pb-2 mx--3 px-4">
          <h4 className="mb-0">Fields</h4>
          <div>
            <Button
              size="sm"
              onClick={() =>
                setModSchema((prev) => ({ ...prev, Fields: uploadedFields }))
              }
            >
              Reset
            </Button>
            <Button
              size="sm"
              color="primary"
              onClick={() => setCreateDrawer(true)}
            >
              Add Field
            </Button>
          </div>
        </div>
        <FieldsList
          fields={modSchema.Fields}
          onEdit={(f: Field) => setEditField(f)}
          onDelete={(field: Field) =>
            setModSchema((m) => ({
              ...m,
              Fields: m.Fields.filter((f) => f.APIName !== field.APIName),
            }))
          }
          emptyMessage="No fields have been added."
        />
      </CollapsibleGroup>
      <CollapsibleGroup
        icon="mdi mdi-database-edit-outline"
        title="Record Name Settings"
        description="Configure the fields that should be shown for each record's name."
        className="mb-4"
        startOpen={false}
      >
        <RecordNameTemplate
          currentModule={modSchema}
          customSave={(props: any) => {
            const newDetailView = {
              ...modSchema.Templates.RecordDetailView,
            };

            if (props.recordName) {
              newDetailView.PageTitle = props.recordName;
            }
            if (props.recordNameImage) {
              newDetailView.ImageField = props.recordNameImage;
            }
            if (props.recordNameMetadata) {
              newDetailView.MetadataField = props.recordNameMetadata;
            }

            setModSchema((m) => ({
              ...m,
              Templates: { ...m.Templates, RecordDetailView: newDetailView },
            }));
            callFunctionIfExists(props.onSuccess);
          }}
        />
      </CollapsibleGroup>
      <CreateFieldDrawer
        open={createDrawer}
        onClose={() => setCreateDrawer(false)}
        onSave={(newF: Field, onSuccess: any) => {
          const newFields = createField(newF, modSchema.Fields);
          moveFirstToLast(newFields);
          setModSchema((m) => ({ ...m, Fields: newFields }));
          setCreateDrawer(false);
          callFunctionIfExists(onSuccess);
        }}
        size="sm"
      />
      <EditFieldDrawer
        open={!!editField}
        onClose={() => setEditField(undefined)}
        // @ts-ignore
        field={editField}
        onSave={(_: any, updatedF: any, onSuccess: any) => {
          const newFields = createField(updatedF, modSchema.Fields);
          const updatedField = newFields[newFields.length - 1];

          setModSchema((m) => ({
            ...m,
            Fields: m.Fields.map((f) =>
              f.APIName === updatedField.APIName ? updatedField : f
            ),
          }));

          callFunctionIfExists(onSuccess);
        }}
        size="sm"
        allowFieldTypeEdit={true}
      />
    </>
  );

  function handleNameChange(e: ChangeEvent<HTMLInputElement>) {
    const value = e.target.value;

    const apiName = StrUtils.cleanAPIName(value);

    setModSchema((prev) => ({ ...prev, Name: value, APIName: apiName }));
  }

  function handleAPINameChange(e: ChangeEvent<HTMLInputElement>) {
    const value = StrUtils.cleanAPIName(e.target.value);

    setModSchema((prev) => ({ ...prev, APIName: value }));
  }
}

function moveFirstToLast(arr: any[]) {
  if (arr.length < 2) return arr;

  const lastElement = arr.pop();
  arr.unshift(lastElement);
}
